import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMyFontsQuery } from '@/generated/graphql';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Type, AlertCircle, Search, ChevronDown, Check } from 'lucide-react';

interface FontOption {
  id: string;
  name: string;
  family: string;
  file_url: string;
  preview_black_url?: string | null;
  preview_white_url?: string | null;
}

interface FontSelectorProps {
  value?: string;
  onValueChange?: (fontId: string, font: FontOption) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  showPreview?: boolean;
  disabled?: boolean;
}

const FontSelector = ({
  value,
  onValueChange,
  label: labelProp,
  placeholder: placeholderProp,
  className,
  showPreview = true,
  disabled = false,
}: FontSelectorProps) => {
  const { t } = useTranslation();
  const label = labelProp ?? t('fontSelector.selectFont');
  const placeholder = placeholderProp ?? t('fontSelector.chooseFontPlaceholder');
  const { data, loading, error } = useMyFontsQuery();
  const [selectedFont, setSelectedFont] = useState<FontOption | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Font weight mapping for sorting (heaviest to lightest)
  const getFontWeight = (fontName: string): number => {
    const name = fontName.toLowerCase();

    // Define weight patterns with their numeric values (higher = heavier)
    const weightPatterns = [
      { patterns: ['black', 'heavy'], weight: 900 },
      { patterns: ['extra bold', 'extrabold', 'ultra bold', 'ultrabold'], weight: 800 },
      { patterns: ['bold'], weight: 700 },
      { patterns: ['semi bold', 'semibold', 'demi bold', 'demibold', 'medium bold'], weight: 600 },
      { patterns: ['medium'], weight: 500 },
      { patterns: ['regular', 'normal', 'book'], weight: 400 },
      { patterns: ['light'], weight: 300 },
      { patterns: ['extra light', 'extralight', 'ultra light', 'ultralight'], weight: 200 },
      { patterns: ['thin', 'hairline'], weight: 100 },
    ];

    // Check for weight patterns in font name
    for (const { patterns, weight } of weightPatterns) {
      for (const pattern of patterns) {
        if (name.includes(pattern)) {
          return weight;
        }
      }
    }

    // Default to regular weight if no pattern matches
    return 400;
  };

  // Group fonts by family and filter by search term
  const fontFamilies = useMemo(() => {
    if (!data?.myFonts) return [];

    // Filter fonts based on search term first
    const filteredFonts = data.myFonts.filter((font) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      const nameMatch = font.name.toLowerCase().includes(searchLower);
      const familyMatch = font.family?.toLowerCase().includes(searchLower);

      return nameMatch || familyMatch;
    });

    const familyMap = new Map<string, FontOption[]>();

    filteredFonts.forEach((font) => {
      const family = font.family || t('fontSelector.noFamily');
      if (!familyMap.has(family)) {
        familyMap.set(family, []);
      }
      familyMap.get(family)!.push(font);
    });

    // Convert map to array and sort families alphabetically
    return Array.from(familyMap.entries())
      .map(([family, fonts]) => ({
        family,
        fonts: fonts.sort((a, b) => {
          // Sort by font weight (heaviest to lightest)
          const weightA = getFontWeight(a.name);
          const weightB = getFontWeight(b.name);

          if (weightA !== weightB) {
            return weightB - weightA; // Descending order (Black to Thin)
          }

          // If weights are the same, sort alphabetically
          return a.name.localeCompare(b.name);
        })
      }))
      .sort((a, b) => a.family.localeCompare(b.family));
  }, [data?.myFonts, searchTerm]);

  // Count total fonts for search results
  const totalFontsCount = useMemo(() => {
    return fontFamilies.reduce((total, family) => total + family.fonts.length, 0);
  }, [fontFamilies]);

  // Flatten fonts for keyboard navigation
  const flatFonts = useMemo(() => {
    return fontFamilies.flatMap(family => family.fonts);
  }, [fontFamilies]);

  // Find current selected font
  const currentFont = useMemo(() => {
    if (!value || !data?.myFonts) return null;
    return data.myFonts.find(font => font.id === value) || null;
  }, [value, data?.myFonts]);

  // Update search term when a font is selected to show its name
  useEffect(() => {
    if (!isOpen && currentFont) {
      setSearchTerm(currentFont.name);
    }
  }, [currentFont, isOpen]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Reset search term to selected font name if no selection
        if (currentFont) {
          setSearchTerm(currentFont.name);
        } else {
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [currentFont]);

  // Reset focused index when search term changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [searchTerm]);

  const handleValueChange = (fontId: string) => {
    const font = data?.myFonts?.find(f => f.id === fontId);
    if (font && onValueChange) {
      setSelectedFont(font);
      onValueChange(fontId, font);
      setSearchTerm(font.name);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex(prev =>
            prev < flatFonts.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev =>
            prev > 0 ? prev - 1 : flatFonts.length - 1
          );
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (isOpen && focusedIndex >= 0 && flatFonts[focusedIndex]) {
          handleValueChange(flatFonts[focusedIndex].id);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        if (currentFont) {
          setSearchTerm(currentFont.name);
        }
        break;
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchTerm(newValue);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      // Clear search term when focused for easier searching
      if (searchTerm === currentFont?.name) {
        setSearchTerm('');
      }
    }
  };

  if (loading) {
    return (
      <div className={className}>
        {label && <Label className="text-sm font-medium">{label}</Label>}
        <div className="flex items-center justify-center h-10 px-3 py-2 border border-input bg-background rounded-md">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">{t('fontSelector.loadingFonts')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        {label && <Label className="text-sm font-medium">{label}</Label>}
        <div className="flex items-center justify-center h-10 px-3 py-2 border border-destructive bg-destructive/5 rounded-md">
          <AlertCircle className="h-4 w-4 text-destructive mr-2" />
          <span className="text-sm text-destructive">{t('fontSelector.errorLoadingFonts')}</span>
        </div>
      </div>
    );
  }

  if (!data?.myFonts?.length) {
    return (
      <div className={className}>
        {label && <Label className="text-sm font-medium">{label}</Label>}
        <div className="flex items-center justify-center h-10 px-3 py-2 border border-input bg-muted/50 rounded-md">
          <Type className="h-4 w-4 text-muted-foreground mr-2" />
          <span className="text-sm text-muted-foreground">{t('fontSelector.noFontsAvailable')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && <Label className="text-sm font-medium mb-2 block">{label}</Label>}

      {/* Combobox */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            className="pl-10 pr-10"
            disabled={disabled}
            autoComplete="off"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-[300px] overflow-auto"
          >
            {fontFamilies.length === 0 ? (
              // No results message
              <div className="px-4 py-8 text-center">
                <Type className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-1">
                  {t('fontSelector.noFontsFound')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('fontSelector.tryDifferentSearch')}
                </p>
              </div>
            ) : (
              <>
                {/* Search results count */}
                {searchTerm && (
                  <div className="px-3 py-2 text-xs text-muted-foreground border-b bg-muted/50">
                    {totalFontsCount === 0
                      ? t('fontSelector.noResultsFound')
                      : t('fontSelector.fontsFound_other', { count: totalFontsCount })
                    }
                  </div>
                )}

                {fontFamilies.map((familyGroup, familyIndex) => (
                  <div key={familyGroup.family}>
                    {/* Family header */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                      {familyGroup.family} ({familyGroup.fonts.length})
                    </div>

                    {/* Fonts in family */}
                    {familyGroup.fonts.map((font, fontIndex) => {
                      const flatIndex = fontFamilies
                        .slice(0, familyIndex)
                        .reduce((acc, fam) => acc + fam.fonts.length, 0) + fontIndex;
                      const isSelected = value === font.id;
                      const isFocused = focusedIndex === flatIndex;

                      return (
                        <button
                          key={font.id}
                          type="button"
                          className={`w-full px-6 py-2 text-left hover:bg-accent/50 transition-colors flex items-center gap-2 min-h-[36px] ${
                            isFocused ? 'bg-accent' : ''
                          } ${isSelected ? 'bg-accent/70' : ''}`}
                          onClick={() => handleValueChange(font.id)}
                        >
                          <div className="flex items-center gap-2 w-full">
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary flex-shrink-0" />
                            )}
                            {font.preview_white_url ? (
                              // Show preview image when available
                              <div className="flex items-center gap-2 w-full">
                                <img
                                  src={font.preview_white_url}
                                  alt={`Preview - ${font.name}`}
                                  crossOrigin="anonymous"
                                  className="h-6 w-auto max-w-[120px] object-contain bg-gray-900 rounded px-1"
                                  style={{ maxHeight: '24px' }}
                                />
                                <span className="text-xs text-muted-foreground truncate ml-auto">
                                  {font.name}
                                </span>
                              </div>
                            ) : (
                              // Fallback to text display
                              <div className="flex items-center gap-2 w-full">
                                <Type className="h-3 w-3 flex-shrink-0" />
                                <span className="flex-1 truncate text-sm">{font.name}</span>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Font preview */}
      {showPreview && (selectedFont || currentFont) && (
        <Card className="mt-3 p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{t('fontSelector.fontPreview')}</h4>
              <Badge variant="outline" className="text-xs">
                {(selectedFont || currentFont)?.family}
              </Badge>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {/* Preview with black text */}
              {(selectedFont || currentFont)?.preview_white_url && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('fontSelector.blackText')}</p>
                  <img
                    src={(selectedFont || currentFont)!.preview_white_url!}
                    alt={`Preview preto - ${(selectedFont || currentFont)?.name}`}
                    crossOrigin="anonymous"
                    className="w-full h-auto bg-white border border-border rounded"
                    style={{ maxHeight: '80px', objectFit: 'contain' }}
                  />
                </div>
              )}

              {/* Preview with white text */}
              {(selectedFont || currentFont)?.preview_black_url && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('fontSelector.whiteText')}</p>
                  <img
                    src={(selectedFont || currentFont)!.preview_white_url!}
                    alt={`Preview branco - ${(selectedFont || currentFont)?.name}`}
                    crossOrigin="anonymous"
                    className="w-full h-auto bg-black border border-border rounded"
                    style={{ maxHeight: '80px', objectFit: 'contain' }}
                  />
                </div>
              )}

              {/* Font URL link */}
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-xs text-muted-foreground">{t('fontSelector.fontFile')}</span>
                <a
                  href={(selectedFont || currentFont)?.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  {t('fontSelector.downloadFont')}
                </a>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default FontSelector;
